/**
 * Alert Helper Functions (#670)
 *
 * Shared utilities for deriving display-friendly names and messages from alerts.
 * Backend returns alertId/hostId; these helpers extract human-readable values
 * from labels and annotations.
 *
 * Used by: InfraAlertsPage, InfraHostDetailPage, InfraHostsListPage
 */

import type { ActiveAlert, AlertSeverity } from '@/core/infra_types'

/**
 * Map alert severity to Badge variant for consistent styling across pages.
 */
export const severityVariantMap: Record<AlertSeverity, 'error' | 'warning' | 'info'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
}

/**
 * Severity rank for sorting. Lower number = higher priority.
 */
export const SEVERITY_RANK: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

/**
 * Derive a human-readable alert name from an ActiveAlert.
 * Priority: labels['alertname'] > alertId > fallback
 * Uses || (not ??) so empty strings fall through to the next option.
 */
export function deriveAlertName(alert: ActiveAlert): string {
  return alert.labels['alertname'] || alert.alertId || '(unnamed alert)'
}

/**
 * Derive a human-readable alert message from an ActiveAlert.
 * Priority: annotations['summary'] > annotations['description'] > empty string
 */
export function deriveAlertMessage(alert: ActiveAlert): string {
  return alert.annotations?.['summary'] ?? alert.annotations?.['description'] ?? ''
}

/**
 * Host incident aggregate — groups alerts by host.
 */
export interface HostIncident {
  hostId: string
  hostName: string
  environmentName: string
  alerts: ActiveAlert[]
  maxSeverity: AlertSeverity
  oldestStart: string
}

/**
 * Host lookup type for grouping.
 */
export interface HostLookup {
  id: string
  name: string
  environmentName: string
}

/**
 * Group alerts by host and compute aggregate severity and oldest start time.
 * Sorts by severity (critical first), then by recency (oldest alert first).
 */
export function groupAlertsByHost(
  alerts: ActiveAlert[],
  hosts: HostLookup[]
): HostIncident[] {
  const hostMap = new Map<string, HostIncident>()

  for (const alert of alerts) {
    const existing = hostMap.get(alert.hostId)
    const host = hosts.find(h => h.id === alert.hostId)

    if (existing) {
      existing.alerts.push(alert)
      if (SEVERITY_RANK[alert.severity] < SEVERITY_RANK[existing.maxSeverity]) {
        existing.maxSeverity = alert.severity
      }
      if (new Date(alert.startsAt) < new Date(existing.oldestStart)) {
        existing.oldestStart = alert.startsAt
      }
    } else {
      hostMap.set(alert.hostId, {
        hostId: alert.hostId,
        hostName: host?.name ?? alert.hostId,
        environmentName: host?.environmentName ?? '(unknown)',
        alerts: [alert],
        maxSeverity: alert.severity,
        oldestStart: alert.startsAt,
      })
    }
  }

  const incidents = Array.from(hostMap.values())
  incidents.sort((a, b) => {
    const sevDiff = SEVERITY_RANK[a.maxSeverity] - SEVERITY_RANK[b.maxSeverity]
    if (sevDiff !== 0) return sevDiff
    return new Date(a.oldestStart).getTime() - new Date(b.oldestStart).getTime()
  })

  return incidents
}

/**
 * Sort alerts by severity (critical first), then by recency (oldest first).
 */
export function sortAlertsBySeverityAndTime(alerts: ActiveAlert[]): ActiveAlert[] {
  return [...alerts].sort((a, b) => {
    const sevDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    if (sevDiff !== 0) return sevDiff
    return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
  })
}

/**
 * Partition alerts into active and muted groups.
 * Muted = has mutedUntil in the future.
 */
export function partitionMutedAlerts(
  alerts: ActiveAlert[],
  now: Date = new Date()
): { active: ActiveAlert[]; muted: ActiveAlert[] } {
  const active: ActiveAlert[] = []
  const muted: ActiveAlert[] = []

  for (const alert of alerts) {
    if (alert.mutedUntil && new Date(alert.mutedUntil) > now) {
      muted.push(alert)
    } else {
      active.push(alert)
    }
  }

  return { active, muted }
}

/**
 * Whether an alert is currently active: still firing per Alertmanager's
 * status and not closed by an operator (agion/Agion#2357). This is the one
 * canonical client-side rule for "active" -- it mirrors the server's
 * filterAlerts default (excludes ClosedAt != nil unless include_resolved is
 * set) -- so callers never re-derive the closedAt check themselves.
 */
export function isActiveAlert(alert: ActiveAlert): boolean {
  return alert.status !== 'resolved' && !alert.closedAt
}

/**
 * Count still-firing alerts by severity, for the Active Alerts summary
 * tiles (agion/Agion#2357). An operator's close is independent of
 * Alertmanager's own status field -- Alertmanager can keep reporting
 * "firing" for an alert an operator already closed -- so a closed alert
 * never counts here, whether `alerts` is the default active list or the
 * 24h history view (which also includes closed alerts).
 */
export function countFiringBySeverity(
  alerts: ActiveAlert[]
): Record<AlertSeverity, number> {
  const firing = alerts.filter(a => a.status === 'firing' && isActiveAlert(a))
  return {
    critical: firing.filter(a => a.severity === 'critical').length,
    warning: firing.filter(a => a.severity === 'warning').length,
    info: firing.filter(a => a.severity === 'info').length,
  }
}

/**
 * Format a countdown from now until a target date.
 * Returns human-readable duration like "45m", "2h 30m", "1d 5h", or "expired".
 */
export function formatCountdown(until: Date, now: Date = new Date()): string {
  const diffMs = until.getTime() - now.getTime()
  if (diffMs <= 0) return 'expired'

  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 60) return `${diffMins}m`

  const diffHours = Math.floor(diffMins / 60)
  const remainingMins = diffMins % 60
  if (diffHours < 24) return `${diffHours}h ${remainingMins}m`

  const diffDays = Math.floor(diffHours / 24)
  const remainingHours = diffHours % 24
  return `${diffDays}d ${remainingHours}h`
}

/**
 * Check if an alert is currently muted (mutedUntil is in the future).
 */
export function isAlertMuted(alert: ActiveAlert, now: Date = new Date()): boolean {
  return Boolean(alert.mutedUntil && new Date(alert.mutedUntil) > now)
}

/** One operator status of an alert, keyed like its i18n label. */
export interface AlertOperatorStatus {
  key: 'onIt' | 'mitigated' | 'closed'
  by: string
  at?: string
}

/**
 * The operator statuses set on an alert, in the order operators use them:
 * "I'm on it", "Mitigated", "Resolved" (agion/Agion#2357). The Slack message
 * shows the same statuses from the same store.
 */
export function alertOperatorStatuses(alert: ActiveAlert): AlertOperatorStatus[] {
  const candidates: Array<[AlertOperatorStatus['key'], string | undefined, string | undefined]> = [
    ['onIt', alert.ackedBy, alert.ackedAt],
    ['mitigated', alert.mitigatedBy, alert.mitigatedAt],
    ['closed', alert.closedBy, alert.closedAt],
  ]
  return candidates
    .filter(([, by]) => Boolean(by))
    .map(([key, by, at]) => ({ key, by: by as string, ...(at ? { at } : {}) }))
}
